import { Config, clone, coinEntryWindow, countSymbol, emptyHits, previewCascades, visibleWindow } from './preview';
export { clone } from './preview';
export type Coin = { coinType: string; multiplier: number; enabled: boolean; totalWinInCredits: number;
  startWindowIndex: number; history: { type: string; multiplier: number; totalWinInCredits: number }[] };
export type CoinWindow = (Coin | null)[][];
export type TFf035Scenario = { reelIndexes?: number[]; reelSet?: 'freespins'; coinSpin?: CoinWindow; forceCoinSpinsTrigger?: boolean };
export type TFf035EditablePlay = { reelIndexes: number[]; reelSet?: 'freespins'; coinSpins?: CoinWindow[]; forceCoinSpinsTrigger?: boolean };
export const getVisibleWindow = (indexes: number[], config: Config, reelSet?: 'freespins') => visibleWindow(indexes, config, reelSet === 'freespins');
export const countCoins = (window: string[][], config: Config) => countSymbol(window, config.coin);
export const isCoinSpinsEligible = (window: string[][], config: Config) => countCoins(window, config) >= config.coinFeature.terminalCoinTriggerMinimumCoins;
export const coinValue = (coin: Coin) => coin.coinType === 'price' ? String(coin.multiplier) : coin.coinType;
export function makeCoin(value: string | number, index = 0): Coin {
  const price = Number.isFinite(Number(value)); const multiplier = price ? Number(value) : 0;
  const type = price ? 'price' : String(value);
  return { coinType: type, multiplier, enabled: true, totalWinInCredits: 0, startWindowIndex: index,
    history: [{ type, multiplier, totalWinInCredits: 0 }] };
}
export const getConfiguredCoins = (config: Config) => config.coinFeature.coins.map(value => ({ name: String(value), coin: makeCoin(value) }));
export function seedCoinSpinWindows(terminalWindow: string[][], config: Config): CoinWindow[] {
  const first = config.coinFeature.coins.find(value => typeof value === 'number');
  if (first === undefined) throw new Error('Coin Spins needs a configured price coin.');
  return [coinEntryWindow(terminalWindow, config).map(column => column.map(symbol => symbol === config.coin ? makeCoin(first) : null))];
}
export function projectPlays(plays: TFf035EditablePlay[], config: Config) {
  let remaining = 0; let hits = emptyHits(config);
  return plays.map(play => {
    const free = remaining > 0;
    const initialHits = free ? hits : emptyHits(config);
    const steps = previewCascades(play.reelIndexes, config, free || play.reelSet === 'freespins', initialHits);
    const terminal = steps[steps.length - 1]; const window = terminal.window;
    const scatters = countSymbol(window, config.scat);
    const award = scatters >= 3 ? config.freeSpinsAwards[Math.min(scatters, 7)] : 0;
    remaining = Math.max(0, remaining - 1) + award;
    hits = free && remaining > 0 ? terminal.spotHitsAfter : emptyHits(config);
    return { free, steps, window, initialHits, remaining, award, scatters, coins: countCoins(window, config),
      totalWinInCredits: steps.reduce((sum, step) => sum + step.totalWinInCredits, 0) };
  });
}
export const reconcileCoinSpinsEligibility = (play: TFf035EditablePlay, config: Config): TFf035EditablePlay => {
  const view = projectPlays([play], config)[0];
  if (isCoinSpinsEligible(view.window, config)) return play;
  return { reelIndexes: [...play.reelIndexes], ...(play.reelSet ? { reelSet: play.reelSet } : {}) };
};
export function projectCoinSteps(plans: CoinWindow[], config: Config) {
  const shape = config.coinFeature.reelsSetup ?? config.reelsSetup;
  let previous: CoinWindow = shape.map(height => Array(height).fill(null));
  let remaining = config.coinFeature.coinTriggerSpinCount;
  return plans.map((plan, index) => {
    if (index > 0 && remaining === 0) throw new Error('Remove Coin Spin steps after the feature has finished.');
    if (!Array.isArray(plan) || plan.length !== shape.length || plan.some((column, reel) => !Array.isArray(column) || column.length !== shape[reel])) {
      throw new Error('Coin Spin board does not match the configured feature grid.');
    }
    const held = clone(previous); let added = 0;
    const board = plan.map((column, reel) => column.map((coin, row) => {
      const old = previous[reel][row];
      if (old) {
        if (coin && coinValue(coin) !== coinValue(old)) throw new Error('Held Coin values cannot be replaced.');
        return clone(old);
      }
      if (!coin) return null;
      if (!config.coinFeature.coins.map(String).includes(coinValue(coin))) throw new Error('Coin value is not available in this config.');
      if (typeof coin.enabled !== 'boolean' || !Number.isFinite(coin.totalWinInCredits) || coin.totalWinInCredits < 0 ||
        !Number.isFinite(coin.multiplier) || coin.multiplier < 0 || !Array.isArray(coin.history)) throw new Error('Invalid Coin value.');
      added++; return clone(coin);
    }));
    if (index > 0) remaining = added ? config.coinFeature.coinTriggerSpinCount : remaining - 1;
    if (board.every(column => column.every(Boolean))) remaining = 0;
    previous = board;
    return { board, held, remaining, added };
  });
}
export function toNativeScenarioQueue(plays: TFf035EditablePlay[], config: Config): TFf035Scenario[] {
  if (!plays.length) throw new Error('Add at least one play.');
  const views = projectPlays(plays, config);
  return plays.flatMap((play, index) => {
    const view = views[index];
    const result: TFf035Scenario = { reelIndexes: [...play.reelIndexes], forceCoinSpinsTrigger: play.forceCoinSpinsTrigger ?? false };
    if (play.reelSet) result.reelSet = play.reelSet;
    if (!play.coinSpins?.length) {
      if (play.forceCoinSpinsTrigger) throw new Error('Script the Coin Spins entry before forcing its trigger.');
      if (view.coins >= config.coinFeature.coinTrigger) throw new Error('Script Coin Spins for this guaranteed trigger.');
      return [result];
    }
    if (!isCoinSpinsEligible(view.window, config)) throw new Error(`Play ${index + 1}: the final grid has too few coins to trigger Coin Spins.`);
    if (!play.forceCoinSpinsTrigger && view.coins < config.coinFeature.coinTrigger) throw new Error('Enable the Coin Spins trigger for this scripted feature.');
    const entry = coinEntryWindow(view.window, config);
    const projected = projectCoinSteps(play.coinSpins, config);
    if (entry.some((column, reel) => column.some((symbol, row) => (symbol === config.coin) !== !!projected[0].board[reel][row]))) {
      throw new Error('Coin Spins entry must preserve the coins from the final tumble grid. Recreate the Coin Spins script after changing reel stops.');
    }
    const plans = projected.map(step => step.board);
    const last = projected[projected.length - 1];
    // Append misses so the next queued indexed entry cannot be consumed by an unfinished feature.
    for (let miss = 0; miss < last.remaining; miss++) plans.push(clone(last.board));
    result.coinSpin = clone(plans[0]);
    return [result, ...plans.slice(1).map(coinSpin => ({ coinSpin: clone(coinSpin) }))];
  });
}
export function fromNativeScenarioQueue(scenarios: TFf035Scenario[]): TFf035EditablePlay[] {
  if (!Array.isArray(scenarios) || !scenarios.length) throw new Error('This preset has no scenario steps.');
  const plays: TFf035EditablePlay[] = [];
  scenarios.forEach(scenario => {
    if (!scenario || Object.keys(scenario).some(key => !['reelIndexes', 'reelSet', 'coinSpin', 'forceCoinSpinsTrigger'].includes(key))) {
      throw new Error('This preset contains unsupported fields and has not been changed.');
    }
    if (scenario.reelSet !== undefined && scenario.reelSet !== 'freespins') throw new Error('Unsupported reel source.');
    if (scenario.forceCoinSpinsTrigger !== undefined && typeof scenario.forceCoinSpinsTrigger !== 'boolean') throw new Error('Invalid Coin trigger option.');
    if (scenario.reelIndexes) {
      plays.push({ reelIndexes: [...scenario.reelIndexes], ...(scenario.reelSet ? { reelSet: scenario.reelSet } : {}),
        ...(scenario.coinSpin ? { coinSpins: [clone(scenario.coinSpin)] } : {}),
        ...(scenario.forceCoinSpinsTrigger !== undefined ? { forceCoinSpinsTrigger: scenario.forceCoinSpinsTrigger } : {}) });
    } else if (scenario.coinSpin && Object.keys(scenario).length === 1 && plays[plays.length - 1]?.coinSpins) {
      plays[plays.length - 1].coinSpins!.push(clone(scenario.coinSpin));
    } else throw new Error('Coin continuation has no owning play. This preset has not been changed.');
  });
  return plays;
}
export const getScenarioLabel = (plays: TFf035EditablePlay[], config: Config) => {
  const views = projectPlays(plays, config);
  return `${plays.length} play(s), ${views.reduce((sum, view) => sum + view.steps.length - 1, 0)} tumble(s), ${views.reduce((sum, view) => sum + view.coins, 0)} final coin(s)`;
};
